<script setup>
  import { ref } from 'vue';
  import InfoStep from '@/components/registration/InfoStep.vue';
  import CreateUserStep from '@/components/registration/CreateUserStep.vue';
  import AddressStep from '@/components/registration/AddressStep.vue';
  import UserService from '@/services/UserService';
  import InlineErrorMessage from '@/components/error/InlineErrorMessage.vue';
  import Spinner from '@/components/Spinner.vue';
  import userService from '@/services/UserService';
  import ConfirmModal from '@/components/modals/ConfirmModal.vue';

  const props = defineProps({
    platform: {
      type: Object,
      default: () => {},
    },
  });

  const step = ref(1);
  const steps = ref(3);
  const creating = ref(false);
  const linking = ref(false);
  const created = ref(false);
  const error = ref(false);
  const user = ref(null);
  const validationErrors = ref({});
  const consentGiven = ref(false);
  const consentError = ref(false);
  const showCancelConfirm = ref(false);
  const form = ref({
    userData: {
      lastName: null,
      firstName: null,
      email: null,
      password: null,
      birthdate: null,
      sex: '',
      agreeTerms: false,
    },
    userAddress: {
      street: null,
      number: null,
      zip: null,
      city: null,
      country: null,
      phone: null,
    },
  });

  const gotoStep = (newStep) => {
    step.value = newStep;
  };

  function createAccount() {
    validationErrors.value = {};
    creating.value = true;

    UserService.createAccount(form.value)
      .then((res) => {
        creating.value = false;

        if (res.data.id) {
          created.value = true;
          // Set user data from response
          user.value = res.data;
        } else {
          error.value = true;
        }
      })
      .catch((err) => {
        const violations = err.response.data.violations;
        setViolations(violations);
        creating.value = false;
      });
  }

  function setViolations(violations) {
    if (violations && violations.length > 0) {
      violations.forEach((v) => {
        const keyArr = v.propertyPath.split('.');
        if (keyArr.length > 1) {
          if (!validationErrors.value[keyArr[0]]) {
            validationErrors.value[keyArr[0]] = {};
          }
          validationErrors.value[keyArr[0]][keyArr[1]] = v.title;
        } else {
          if (!validationErrors.value['userData']) {
            validationErrors.value['userData'] = {};
          }
          validationErrors.value['userData'][keyArr[0]] = v.title;
        }
      });
      if (violations[0].propertyPath.includes('userData')) {
        gotoStep(2);
      }
    }
  }

  function linkAccount() {
    consentError.value = false;

    if (!consentGiven.value) {
      consentError.value = true;
      return;
    }

    linking.value = true;
    userService.linkAccount().then(() => {
      linking.value = false;
      location.href = '/app';
    });
  }

  function cancelLinking() {
    showCancelConfirm.value = true;
  }

  function confirmCancelLinking() {
    showCancelConfirm.value = false;
    location.href = '/logout';
  }

  function getPrivacyUrl() {
    // Use the main website URL (www) for privacy policy
    const websiteUrl = window.viploge?.urls?.website;
    return websiteUrl ? `${websiteUrl}/nl/privacy` : '/nl/privacy';
  }
</script>

<template>
  <!-- Cancel Linking Confirmation Modal -->
  <ConfirmModal
    :open="showCancelConfirm"
    :submitBtn="$t('auth.platform_linking.cancel_confirm.confirm_button')"
    :title="$t('auth.platform_linking.cancel_confirm.title')"
    :description="$t('auth.platform_linking.cancel_confirm.description')"
    color="red"
    @close="showCancelConfirm = false"
    @confirm="confirmCancelLinking"
  />

  <div v-if="error">
    <InlineErrorMessage
      msg="auth.error.createAccount"
      width="w-80 sm:w-96"
      class="mb-8 mt-8"
    />
    <a class="text-primary-600 underline" href="/register">
      Klik hier op opnieuw te starten
    </a>
  </div>

  <div v-else-if="created" class="w-full">
    <h1 class="text-lg lg:text-xl font-bold text-gray-900">
      {{ $t('auth.platform_linking.account_created') }}
    </h1>

    <p v-if="user" class="mt-4 font-bold text-gray-500">
      {{ $t('auth.platform_linking.greeting') }} {{ user.fullName }}
    </p>

    <p class="mt-4 text-gray-500">
      {{ $t('auth.platform_linking.ready_to_use') }}
    </p>

    <p
      class="mt-4 text-gray-500"
      v-html="
        $t('auth.platform_linking.platform_created_from', {
          platform: props.platform?.name || 'dit platform',
        })
      "
    ></p>

    <p class="mt-4 text-gray-500">
      {{ $t('auth.platform_linking.privacy_info') }}
    </p>

    <!-- Data sharing information -->
    <div class="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <h3 class="mb-3 font-semibold text-gray-700">
        {{ $t('auth.platform_linking.data_shared_title') }}
      </h3>
      <ul class="space-y-2 text-sm text-gray-600">
        <li class="flex items-start">
          <svg
            class="size-4 text-primary mr-2 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clip-rule="evenodd"
            />
          </svg>
          {{ $t('auth.platform_linking.data_shared_list.name') }}
        </li>
        <li class="flex items-start">
          <svg
            class="size-4 text-primary mr-2 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clip-rule="evenodd"
            />
          </svg>
          {{ $t('auth.platform_linking.data_shared_list.email') }}
        </li>
        <li class="flex items-start">
          <svg
            class="size-4 text-primary mr-2 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clip-rule="evenodd"
            />
          </svg>
          {{ $t('auth.platform_linking.data_shared_list.birthdate') }}
        </li>
        <li class="flex items-start">
          <svg
            class="size-4 text-primary mr-2 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clip-rule="evenodd"
            />
          </svg>
          {{ $t('auth.platform_linking.data_shared_list.address') }}
        </li>
        <li class="flex items-start">
          <svg
            class="size-4 text-primary mr-2 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clip-rule="evenodd"
            />
          </svg>
          {{ $t('auth.platform_linking.data_shared_list.phone') }}
        </li>
      </ul>
    </div>

    <!-- Privacy policy link -->
    <div class="mt-4">
      <a
        :href="getPrivacyUrl()"
        target="_blank"
        rel="noopener noreferrer"
        class="text-primary-600 hover:text-primary-800 text-sm hover:underline"
      >
        {{ $t('auth.platform_linking.privacy_link') }}
      </a>
    </div>

    <p
      class="mt-4 text-gray-500"
      v-html="
        $t('auth.platform_linking.platform_access_info', {
          platform: props.platform?.name || 'dit platform',
        })
      "
    ></p>

    <!-- Consent checkbox -->
    <div class="mt-6">
      <div class="flex items-start">
        <input
          id="consentCheckbox"
          v-model="consentGiven"
          type="checkbox"
          class="size-4 text-primary focus:ring-primary mt-1 rounded border-gray-300"
          :class="{
            'border-red-300 text-red-600 focus:ring-red-500': consentError,
          }"
        />
        <label
          for="consentCheckbox"
          class="ml-2 block text-sm leading-5 text-gray-700"
          :class="{
            'text-red-600': consentError,
          }"
        >
          {{ $t('auth.platform_linking.consent_checkbox') }}
        </label>
      </div>
      <p v-if="consentError" class="mt-1 text-sm text-red-600">
        {{ $t('auth.platform_linking.consent_required') }}
      </p>
    </div>

    <!-- Action buttons -->
    <div class="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
      <button
        class="rounded-full px-5 py-3 text-sm flex items-center justify-center w-full bg-primary-600 hover:bg-primary-700 text-white transition-colors"
        :class="{ 'pointer-events-none opacity-50': linking || !consentGiven }"
        :disabled="linking || !consentGiven"
        @click="linkAccount"
      >
        <span>{{ $t('auth.platform_linking.link_button') }}</span>
        <span v-if="linking">
          <Spinner class="size-5 text-white ml-2" />
        </span>
      </button>
      <button
        class="rounded-full px-5 py-3 text-sm w-full border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
        :disabled="linking"
        @click="cancelLinking"
      >
        {{ $t('auth.platform_linking.cancel_button') }}
      </button>
    </div>
  </div>

  <div v-else>
    <div v-if="step === 1">
      <InfoStep :steps @goto-step="gotoStep" />
    </div>

    <div v-if="step === 2">
      <CreateUserStep
        :userData="form.userData"
        :steps
        :validationErrors
        @goto-step="gotoStep"
        @set-violations="setViolations"
      />
    </div>

    <div v-if="step === 3">
      <AddressStep
        :userAddress="form.userAddress"
        :creating
        :steps
        :validationErrors
        @create-account="createAccount"
        @goto-step="gotoStep"
      />
    </div>
  </div>
</template>
